import Accordion from '@/common/components/Accordion'
import MaintenanceObjective from '@/common/components/MaintenanceObjective'
import { formatCurrency, formatPercentage } from '@/common/utils/formatters'
import DiagnosticEntryObjectiveActivities from '@/features/maintenanceWorkflow/pages/Show/components/tabs/ForwardingsTabs/Sections/DirectiveEntry/Content/DiagnosticEntryCompleted/DiagnosticEntryObjectiveActivities'
import DiagnosticEntryObjectivePieces from '@/features/maintenanceWorkflow/pages/Show/components/tabs/ForwardingsTabs/Sections/DirectiveEntry/Content/DiagnosticEntryCompleted/DiagnosticEntryObjectivePieces'
import ExecutionEntryObjectiveActivities from '@/features/maintenanceWorkflow/pages/Show/components/tabs/ForwardingsTabs/Sections/DirectiveEntry/Content/ExecutionEntryCompleted/ExecutionEntryObjectiveActivities'
import ExecutionEntryObjectivePieces from '@/features/maintenanceWorkflow/pages/Show/components/tabs/ForwardingsTabs/Sections/DirectiveEntry/Content/ExecutionEntryCompleted/ExecutionEntryObjectivePieces'
import { MaintenanceForwardingsDirectiveEntryKlass } from '@/features/maintenanceWorkflow/pages/Show/features/forwardings/constants/maintenanceForwardingsDirectiveEntryKlasses.type'
import { MaintenanceForwardingEstablishmentChoiceEntryKlass } from '@/features/maintenanceWorkflow/pages/Show/features/forwardings/types/establishmentChoiceEntries/establishmentChoiceEntry.type'
import { isExecutionEntryProposalContent } from '@/features/maintenanceWorkflow/pages/Show/features/forwardings/types/establishmentChoiceEntries/proposalContents/proposalContent.type'
import {
  MaintenanceForwardingDirectiveEntryDiagnosticEntryData,
  MaintenanceForwardingDirectiveEntryExecutionEntryData,
} from '@/features/maintenanceWorkflow/pages/Show/features/forwardings/types/maintenanceForwarding.type'
import { useForwardingTabContext } from '@/features/maintenanceWorkflow/pages/Show/hooks/forwardings/useForwardingTabContext'
import { ArrowDownIcon, ArrowUpIcon } from 'lucide-react'
import { useMemo } from 'react'

const EstablishmentChoiceEntryProposalDiagnostic = () => {
  const { forwarding, forwardingUpdatedAt, directiveEntryKlass } = useForwardingTabContext<
    MaintenanceForwardingsDirectiveEntryKlass,
    MaintenanceForwardingEstablishmentChoiceEntryKlass.ManualEntry
  >()

  const objectivesCountDisplay = useMemo(() => {
    if (Number(forwarding?.directive_entry?.objectives?.length) > 0) {
      if (Number(forwarding?.directive_entry?.objectives?.length) === 1) {
        return '(1 Objeto)'
      }

      return `(${Number(forwarding?.directive_entry?.objectives?.length)} Objetos)`
    }

    return ''
  }, [forwarding?.directive_entry?.objectives?.length])

  const totalValueDisplay = useMemo(() => {
    if (directiveEntryKlass === MaintenanceForwardingsDirectiveEntryKlass.DiagnosticEntry) {
      const diagnosticEntry = forwarding?.directive_entry as MaintenanceForwardingDirectiveEntryDiagnosticEntryData

      return <span className="text-valid">{formatCurrency(Number(diagnosticEntry?.total_value) || 0)}</span>
    }

    if (directiveEntryKlass === MaintenanceForwardingsDirectiveEntryKlass.ExecutionEntry) {
      const executionEntry = forwarding?.directive_entry as MaintenanceForwardingDirectiveEntryExecutionEntryData
      const proposalStatus = forwarding?.establishment_choice_entry?.actual_proposal?.status
      const proposalContent = forwarding?.establishment_choice_entry?.actual_proposal?.proposal_content

      const isExecutionProposal = isExecutionEntryProposalContent(proposalContent, proposalStatus)
      const newTotalValue = isExecutionProposal ? proposalContent.new_total_value : null
      const differencePercentage = isExecutionProposal ? proposalContent.difference_percentage : null
      const originalTotalValue = executionEntry?.diagnostic?.total_value

      const newTotalValueNumber = Number(newTotalValue) || 0
      const originalTotalValueNumber = Number(originalTotalValue) || 0

      if (newTotalValueNumber !== originalTotalValueNumber) {
        const newValueClassName =
          newTotalValueNumber > originalTotalValueNumber
            ? 'text-danger-soft'
            : newTotalValueNumber < originalTotalValueNumber
              ? 'text-valid'
              : ''

        const differencePercentageNumber = differencePercentage != null ? Number(differencePercentage) : null

        const showDifferencePercentage = differencePercentageNumber != null && differencePercentageNumber !== 0

        return (
          <>
            <span className={newValueClassName}>{formatCurrency(newTotalValueNumber)}</span>

            <span className="text-[10px] text-white-400 line-through ml-1">
              {formatCurrency(originalTotalValueNumber)}
            </span>

            {showDifferencePercentage && (
              <span className="text-[9px] text-white-800 rounded-full px-1 ml-1 bg-yellow-op-2 inline-flex items-center">
                {differencePercentageNumber > 0 ? (
                  <ArrowUpIcon className="size-3 text-danger-soft" />
                ) : (
                  <ArrowDownIcon className="size-3 text-valid" />
                )}
                {formatPercentage(Math.abs(differencePercentageNumber))}
              </span>
            )}
          </>
        )
      }

      return <span className="text-valid">{formatCurrency(Number(originalTotalValue) || 0)}</span>
    }

    return null
  }, [directiveEntryKlass, forwarding?.directive_entry, forwarding?.establishment_choice_entry])

  return (
    <Accordion
      className="p-0"
      triggerClassName="p-4"
      bodyClassName="p-4 pt-0"
      defaultOpen
      head={
        <div className="flex items-center justify-between w-full mr-2">
          <p>Orçamento {objectivesCountDisplay}</p>

          <div className="flex items-center gap-0.5 text-xs">
            <span>Valor total</span>

            <span>•</span>

            {totalValueDisplay}
          </div>
        </div>
      }
      body={
        <div className="flex flex-col gap-1.5">
          {forwarding?.directive_entry?.objectives?.map((objective) => (
            <Accordion
              key={objective.id}
              defaultOpen
              className="p-0 rounded-lg"
              triggerClassName="p-4"
              bodyClassName="p-4 pt-0"
              head={
                <MaintenanceObjective
                  systemDisplayName={objective.system.display_name}
                  actionDisplayName={objective.action.display_name}
                  partDisplayName={objective.part.display_name}
                  actionDescription={objective.action.description}
                />
              }
              body={
                <div className="flex flex-col gap-3">
                  {directiveEntryKlass === MaintenanceForwardingsDirectiveEntryKlass.DiagnosticEntry && (
                    <>
                      <DiagnosticEntryObjectivePieces objective={objective} dataUpdatedAt={forwardingUpdatedAt} />

                      <DiagnosticEntryObjectiveActivities objective={objective} dataUpdatedAt={forwardingUpdatedAt} />
                    </>
                  )}

                  {directiveEntryKlass === MaintenanceForwardingsDirectiveEntryKlass.ExecutionEntry && (
                    <>
                      <ExecutionEntryObjectivePieces objective={objective} dataUpdatedAt={forwardingUpdatedAt} />

                      <ExecutionEntryObjectiveActivities objective={objective} dataUpdatedAt={forwardingUpdatedAt} />
                    </>
                  )}
                </div>
              }
            />
          ))}
        </div>
      }
    />
  )
}

export default EstablishmentChoiceEntryProposalDiagnostic
